// SECURITY: localStorage interceptor to prevent price manipulation
// This completely blocks ANY attempt to store price data in localStorage

interface SecureCartItem {
  id: number;
  quantity: number;
}

// Store original localStorage methods
const originalSetItem = localStorage.setItem.bind(localStorage);
const originalGetItem = localStorage.getItem.bind(localStorage);

// Security function to clean any cart data
function sanitizeCartData(data: any): SecureCartItem[] {
  if (!Array.isArray(data)) return [];
  
  return data
    .filter((item: any) => item && typeof item.id === 'number' && typeof item.quantity === 'number')
    .map((item: any) => ({
      id: item.id,
      quantity: item.quantity
      // Explicitly exclude ALL other fields including:
      // price, final_price, original_price, name, sku, categories, images, etc.
    }));
}

// Override localStorage.setItem to intercept cart writes
localStorage.setItem = function(key: string, value: string) {
  if (key === 'lillaflora_cart') {
    try {
      const parsedValue = JSON.parse(value);
      
      // Check if data contains manipulated fields
      const hasManipulatedData = Array.isArray(parsedValue) && parsedValue.some((item: any) => 
        typeof item.price === 'number' ||
        typeof item.final_price === 'number' ||
        typeof item.original_price === 'number' ||
        item.price !== undefined ||
        item.final_price !== undefined ||
        item.original_price !== undefined ||
        item.name !== undefined ||
        item.sku !== undefined ||
        item.categories !== undefined ||
        item.images !== undefined ||
        item.has_active_discount !== undefined
      );
      
      if (hasManipulatedData) {
        console.warn('🛡️ SECURITY INTERCEPTOR: Blocked attempt to store manipulated cart data!');
        console.warn('🔒 CLEANING: Removing all price and product data, keeping only IDs and quantities');
        
        // Clean the data and store only secure format
        const cleanData = sanitizeCartData(parsedValue);
        originalSetItem(key, JSON.stringify(cleanData));
        
        // Alert in console about the security action
        console.warn('✅ SECURITY: Cart data sanitized. Prices will be fetched from secure backend.');
        return;
      }
    } catch (error) {
      console.error('Error parsing cart data:', error);
      originalSetItem(key, '[]'); // Store empty cart if parsing fails
      return;
    }
  }
  
  // For non-cart data or already clean cart data, use original method
  originalSetItem(key, value);
};

// Override localStorage.getItem to ensure cart data is always clean
localStorage.getItem = function(key: string): string | null {
  const value = originalGetItem(key);
  
  if (key === 'lillaflora_cart' && value) {
    try {
      const parsedValue = JSON.parse(value);
      
      // Check if stored data has been tampered with
      const hasManipulatedData = Array.isArray(parsedValue) && parsedValue.some((item: any) => 
        item.price !== undefined ||
        item.final_price !== undefined ||
        item.original_price !== undefined ||
        item.name !== undefined
      );
      
      if (hasManipulatedData) {
        console.warn('🛡️ SECURITY: Detected manipulated cart data on read, cleaning...');
        const cleanData = sanitizeCartData(parsedValue);
        originalSetItem(key, JSON.stringify(cleanData));
        return JSON.stringify(cleanData);
      }
    } catch (error) {
      console.error('Error parsing cart data on read:', error);
      originalSetItem(key, '[]');
      return '[]';
    }
  }
  
  return value;
};

// AGGRESSIVE security: Monitor for ANY manipulation attempts
const securityMonitor = setInterval(() => {
  try {
    const cartData = originalGetItem('lillaflora_cart');
    if (cartData) {
      const parsed = JSON.parse(cartData);
      const hasManipulation = Array.isArray(parsed) && parsed.some((item: any) => 
        item.price !== undefined || 
        item.final_price !== undefined || 
        item.original_price !== undefined ||
        item.name !== undefined ||
        item.sku !== undefined ||
        item.categories !== undefined ||
        item.images !== undefined ||
        item.has_active_discount !== undefined ||
        typeof item.price === 'number' ||
        typeof item.final_price === 'number' ||
        typeof item.original_price === 'number'
      );
      
      if (hasManipulation) {
        console.error('🚨 SECURITY ALERT: Detected localStorage manipulation - CLEARING ALL CART DATA!');
        console.error('🛡️ BLOCKING: All product data, names, prices, SKUs detected and removed');
        
        // AGGRESSIVE: Clear all cart data when manipulation detected
        const cleaned = sanitizeCartData(parsed);
        originalSetItem('lillaflora_cart', JSON.stringify(cleaned));
        
        // Also fire a custom event to notify the app
        window.dispatchEvent(new CustomEvent('cart-manipulation-detected', {
          detail: { originalData: parsed, cleanedData: cleaned }
        }));
        
        console.error('🔒 SECURITY: Cart manipulation blocked and data cleaned');
      }
    }
  } catch (error) {
    // If any error occurs, clear the cart completely for security
    originalSetItem('lillaflora_cart', '[]');
  }
}, 500); // Check every 500ms for more aggressive monitoring

// Additional protection: Prevent direct localStorage access to cart
Object.defineProperty(window, 'localStorage', {
  value: localStorage,
  writable: false,
  configurable: false
});

// Block direct access to Storage prototype manipulation - DISABLED DUE TO RECURSION
/*
if (typeof Storage !== 'undefined') {
  const originalStorageSetItem = Storage.prototype.setItem;
  Storage.prototype.setItem = function(key: string, value: string) {
    if (key === 'lillaflora_cart') {
      // Route through our secure interceptor using original method
      originalStorageSetItem.call(this, key, value);
      return;
    }
    originalStorageSetItem.call(this, key, value);
  };
}
*/

console.log('🛡️ SECURITY: localStorage interceptor activated - price manipulation blocked');
console.log('🔒 SECURITY: Direct localStorage access protection enabled');

export default {
  cleanup: () => clearInterval(securityMonitor)
};